import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Position } from '@zero/shared/util-models';
import { Observable } from 'rxjs';
import { positionsActions } from './positions.actions';
import { positionsFeature } from './positions.reducers';
import {
  selectCryptos,
  selectCryptosHaveNotes,
  selectPerformanceReady,
  selectPositionByIsin,
  selectPositions,
  selectPositionsHaveNotes,
  selectPositionStocksISINs,
} from './positions.selectors';

@Injectable({ providedIn: 'root' })
export class PositionsFacade {
  private readonly store = inject(Store);

  public readonly allPositions$ = this.store.select(positionsFeature.selectAll);
  public readonly positions$ = this.store.select(selectPositions);
  public readonly positionISINs$ = this.store.select(positionsFeature.selectIds) as Observable<string[]>;
  public readonly cryptos$ = this.store.select(selectCryptos);
  public readonly positionsLoading$ = this.store.select(positionsFeature.selectPositionsLoading);
  public readonly positionsLoadingError$ = this.store.select(positionsFeature.selectPositionsLoadingError);
  public readonly positionsInitialLoaded$ = this.store.select(positionsFeature.selectPositionsInitialLoaded);
  public readonly portfolioPerformance$ = this.store.select(positionsFeature.selectPerformance);
  public readonly performanceReady$ = this.store.select(selectPerformanceReady);
  public readonly positionStocksISINs$ = this.store.select(selectPositionStocksISINs);
  public readonly positionsNotesLoading$ = this.store.select(positionsFeature.selectNotesLoading);
  public readonly positionsHaveNotes$ = this.store.select(selectPositionsHaveNotes);
  public readonly cryptosHaveNotes$ = this.store.select(selectCryptosHaveNotes);
  public readonly positionsCategories$ = this.store.select(positionsFeature.selectCategories);

  public getPositionByIsin$(isin: string): Observable<Position> {
    return this.store.select(selectPositionByIsin(isin));
  }

  public loadPositions(force = false): void {
    this.store.dispatch(positionsActions.loadPositions({ force }));
  }

  public subscribeToPositionQuotes(): void {
    this.store.dispatch(positionsActions.subscribeToPositionQuotes());
  }

  public unsubscribeFromPositionQuotes(): void {
    this.store.dispatch(positionsActions.unsubscribeFromPositionQuotes());
  }

  public subscribeToPositionQuotesByIsin(isin: string): void {
    this.store.dispatch(positionsActions.subscribeToPositionQuote({ isin }));
  }

  public unsubscribeFromPositionQuotesByIsin(isin: string): void {
    this.store.dispatch(positionsActions.unsubscribeFromPositionQuote({ isin }));
  }

  public loadPositionNotes(force = false): void {
    this.store.dispatch(positionsActions.loadPositionsNotes({ force }));
  }

  public loadPositionNoteByISIN(isin: string): void {
    this.store.dispatch(positionsActions.loadPositionNoteByIsin({ isin }));
  }

  public updatePositionsNote(isin: string, note: string): void {
    this.store.dispatch(positionsActions.updatePositionNote({ isin, note }));
  }

  public updatePositionEntryQuote(isin: string, entryQuote: number): void {
    this.store.dispatch(positionsActions.updatePositionEntryQuote({ isin, entryQuote }));
  }
}
